/**
 * Scorecard primitives (epic v3 — executive scorecards).
 *
 * The atoms behind the client's health-scorecard mockups: a track+fill
 * meter and a pastel status pill. Both take ONE `toneSpec` rather than
 * a `tone` + `thresholds` prop pair, so every surface in the epic
 * shares a single tone contract (see `resolveTone` in shared.jsx).
 *
 * Two rules these components exist to enforce:
 *
 *   1. A bare value NEVER implies a colour. Threshold tinting happens
 *      only when the CALLER supplied bands — the value-based cell
 *      styling this codebase built and then deleted must not return
 *      through a new component.
 *   2. The band judgement is never colour-ONLY. `toneLabel` rides into
 *      the accessible name, because a red 72% and a green 94% are
 *      otherwise indistinguishable to a colourblind or screen-reader
 *      reader — on a report that goes to an executive committee.
 *
 * Rendered as HTML (not SVG): these are chrome, not charts. `MeterBar`
 * therefore uses `role="progressbar"` with `aria-valuenow/min/max`,
 * which states the value natively instead of hiding it behind the
 * `role="img"` + label convention the SVG charts use.
 */
import { useState } from 'react';

import { brand } from '../brand.js';
import { MOTION, useCountUp, useEntrance, usePrefersReducedMotion } from './motion.jsx';
import { CursorTip, NEUTRAL, formatValue, resolveTone, toneStyle } from './shared.jsx';

/** The decorative (non-threshold) tint for `Callout accent="info"`.
 *  The kit's primary soft / line / ink, so the executive-readout panel
 *  reads as brand chrome and not as a fifth semantic state. */
const ACCENT_INFO = {
  bg: brand.primary.soft,
  border: brand.primary.line,
  // Through the tenant variable (default `brand.primary.ink`), like the
  // `border-primary-line` class beside it.
  fg: 'rgb(var(--bi-primary-ink))',
  surface: brand.semantic.surfaceInfo,
};

/**
 * Entrance (ronda 14): subtle fade+rise for the TEXT primitives of the
 * scorecard family — `MOTION.durBase` (they are text, not a value
 * animation; 1000ms would delay reading). Returns a style fragment to
 * spread on the component's root. Hook — call before any early return.
 */
function useRiseInStyle(entrance) {
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  const shown = !entrance || entered;
  return {
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : 'translateY(4px)',
    transition: reduced
      ? undefined
      : `opacity ${MOTION.durBase}ms ${MOTION.easeOut}, transform ${MOTION.durBase}ms ${MOTION.easeOut}`,
  };
}

/** Clamp to [0, 1]; anything non-finite reads as 0 (an empty track). */
function fillRatio(value, max) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  const top = typeof max === 'number' && Number.isFinite(max) && max > 0 ? max : 100;
  return Math.max(0, Math.min(1, value / top));
}

/**
 * `MeterBar` — one rounded track, one tone-coloured fill, an optional
 * left label column and an optional right-aligned value.
 *
 * A null/undefined value renders an EMPTY track and an em-dash, never
 * a zero-width fill next to a "0" — missing and zero are different
 * facts and a scorecard must not blur them.
 */
export function MeterBar({
  value,
  max = 100,
  toneSpec,
  label,
  labelWidth = 0,
  height = 8,
  showValue = false,
  valueLabel,
  format = 'raw',
  decimals = 1,
  ariaLabel,
  toneLabel,
  hoverText,
  className = '',
}) {
  const reduced = usePrefersReducedMotion();
  const entered = useEntrance();
  // Opt-in hover tooltip (CHS ronda 3, PORTALED in ronda 5): when
  // `hoverText` is set, the track shows a cursor-anchored CursorTip —
  // portaled to body so it never grows a card scroller, and WITHOUT
  // the native `title` attr (it doubled the readout with the
  // browser's own tooltip). Keyboard and screen-reader users get the
  // reading via aria-valuetext.
  const [hover, setHover] = useState(null); // {x, y} in viewport coords
  const hasValue = typeof value === 'number' && Number.isFinite(value);
  const tone = resolveTone(toneSpec, hasValue ? value : undefined);
  const palette = toneStyle(tone);
  const ratio = fillRatio(value, max);
  // Count-up (ronda 14 follow-up): the value label counts 0 → value on
  // mount, formatted each frame by the SAME deterministic formatter as
  // the static path (landed frame is byte-identical). A caller-supplied
  // `valueLabel` is an opaque string — rendered as-is, never animated.
  const counted = useCountUp(hasValue ? value : null, { disabled: valueLabel != null });
  const text = hasValue
    ? (valueLabel ?? formatValue(counted, format, decimals))
    : '—';
  // A11y — the BI rule applied to the AUDITORY channel (audit
  // 2026-07-24): the visual label may show an intermediate count-up
  // frame, but ARIA must NEVER publish one. A screen-reader user has no
  // way to tell a half-animated number from the datum, and a meter that
  // announces "61%" on its way to 94% is exactly the "valor intermedio
  // como si fuera el dato" the entrance guards exist to prevent. So
  // `aria-valuetext` is built from the REAL final `value`, formatted by
  // the SAME deterministic formatter as the visual path (`aria-valuenow`
  // already carried the real value).
  const finalText = hasValue
    ? (valueLabel ?? formatValue(value, format, decimals))
    : '—';
  const trackPx = Math.max(2, Number.isFinite(height) ? height : 8);
  // The accessible name always carries the band, so the judgement
  // survives without colour. Falls back to the raw tone token rather
  // than to nothing when the caller supplied no translated label.
  const bandText = toneLabel || (toneSpec ? tone : '');
  const valueText = hasValue
    ? `${finalText}${bandText ? ` — ${bandText}` : ''}`
    : '—';

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {labelWidth > 0 && (
        // `labelWidth` is a CEILING, not a fixed width. As a fixed
        // width it both wasted space on short labels ("US" in a 72px
        // column) and truncated long ones that would otherwise have
        // fit — "MERCADOLIBRE" needed 93px against a 72px box and lost
        // its tail. maxWidth keeps the column from eating the meter
        // while letting the label use exactly what it needs.
        <span
          className="shrink-0 truncate text-xs text-neutral-600"
          style={{ maxWidth: labelWidth }}
          title={label || undefined}
        >
          {label}
        </span>
      )}
      <div
        className="relative min-w-0 flex-1 rounded-full"
        style={{ height: trackPx, background: NEUTRAL.track }}
        role="progressbar"
        aria-label={ariaLabel}
        aria-valuemin={0}
        aria-valuemax={typeof max === 'number' && Number.isFinite(max) && max > 0 ? max : 100}
        {...(hasValue ? { 'aria-valuenow': value } : {})}
        aria-valuetext={valueText}
        onMouseMove={
          hoverText
            ? (e) => setHover({ x: e.clientX, y: e.clientY })
            : undefined
        }
        onMouseLeave={hoverText ? () => setHover(null) : undefined}
      >
        {/* Inner wrapper keeps the rounded clip (the tooltip below must
            escape the track, so the clip cannot live on the hover
            target itself). The fill is full-width and SCALED, not
            width-animated: `transform` is compositor-only, so a meter
            easing to its new value on a filter change costs no layout.
            Motion is feedback here — it shows the value moved — so it
            obeys the reduced-motion preference like every other
            animation. */}
        <div className="h-full overflow-hidden rounded-full">
          <div
            className="h-full origin-left rounded-full"
            style={{
              width: '100%',
              // Entrance (ronda 12): first paint at zero, then the
              // transform transition grows the fill to its value.
              // Subsequent value changes morph in place.
              transform: `scaleX(${entered ? ratio : 0})`,
              background: palette.fill,
              transition: reduced ? undefined : `transform ${MOTION.durEntrance}ms ${MOTION.easeOut}`,
            }}
          />
        </div>
        {hoverText && hover && (
          <CursorTip x={hover.x} y={hover.y}>{hoverText}</CursorTip>
        )}
      </div>
      {showValue && (
        <span
          className="shrink-0 text-xs font-bold tabular-nums"
          style={{ color: hasValue ? palette.fg : NEUTRAL.faint }}
        >
          {text}
        </span>
      )}
    </div>
  );
}

/**
 * `StatusChip` — a bare pastel label pill in one tone.
 *
 * Deliberately NOT any of the three pills that already exist:
 * `DeltaChip` carries an arrow and delta semantics, `FreshnessBadge`
 * carries a pulse dot and a staleness vocabulary, and `StatusTable`'s
 * pill is bound to a table row. This one carries arbitrary text —
 * "96/100", "High", "Medium" — in a threshold tone.
 */
/**
 * `ToneIcon` — the band state as a SHAPE, not just a colour.
 *
 * Product owner is red-green colour-blind (2026-07-21): every scorecard
 * band signal must survive without hue. ✓ (good) / ! in a triangle
 * (warn) / × (bad) / – (neutral), tinted with the band colour but
 * legible in pure greyscale. Pure inline SVG — no icon dependency.
 * `tone` accepts an already-resolved token — the caller runs the tone
 * resolver; this component never derives a tone from a raw value.
 */
export function ToneIcon({ tone = 'neutral', size = 16, className = '', ariaLabel }) {
  const palette = toneStyle(tone);
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 20 20',
    role: 'img',
    'aria-label': ariaLabel || `state: ${tone}`,
    'data-tone': tone,
    className,
  };
  if (tone === 'good') {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="9" fill={palette.fill} />
        <path d="M5.5 10.5l3 3 6-6.5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (tone === 'warn') {
    return (
      <svg {...common}>
        <path d="M10 2L18.6 17H1.4L10 2z" fill={palette.fill} />
        <rect x="9" y="7.2" width="2" height="5.2" rx="1" fill="white" />
        <circle cx="10" cy="14.6" r="1.15" fill="white" />
      </svg>
    );
  }
  if (tone === 'info' || tone === 'info_light') {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="9" fill={palette.fill} />
        <circle cx="10" cy="6.2" r="1.25" fill="white" />
        <rect x="9" y="8.6" width="2" height="6.2" rx="1" fill="white" />
      </svg>
    );
  }
  if (tone === 'bad') {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="9" fill={palette.fill} />
        <path d="M6.8 6.8l6.4 6.4M13.2 6.8l-6.4 6.4" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="10" cy="10" r="9" fill={NEUTRAL.track} />
      <rect x="5.5" y="9" width="9" height="2" rx="1" fill={NEUTRAL.muted} />
    </svg>
  );
}

export function StatusChip({
  label,
  toneSpec,
  value,
  toneLabel,
  size = 'sm',
  entrance = true,
  className = '',
}) {
  // Hooks before the early return per the motion rules.
  const riseStyle = useRiseInStyle(entrance);
  if (label == null || label === '') return null;
  const tone = resolveTone(toneSpec, value);
  const palette = toneStyle(tone);
  const pad =
    size === 'xs' ? 'px-1.5 py-0 text-2xs'
      : size === 'md' ? 'px-2.5 py-1 text-xs'
        : 'px-2 py-0.5 text-[11px]';
  // The band as text, for readers who cannot use the tint. Rendered
  // visually hidden so the chip stays as compact as it looks designed.
  const bandText = toneLabel || (toneSpec ? tone : '');

  return (
    <span
      className={`inline-flex items-center rounded-full border font-bold leading-tight whitespace-nowrap tabular-nums ${pad} ${className}`}
      style={{ color: palette.fg, background: palette.bg, borderColor: palette.border, ...riseStyle }}
    >
      {label}
      {bandText && <span className="sr-only"> — {bandText}</span>}
    </span>
  );
}

/**
 * `MetaGrid` — a static labelled key/value grid.
 *
 * Nothing in the catalog rendered one: the nearest thing,
 * `ActiveFiltersBar`, is an interactive filter-removal widget with
 * hardcoded Spanish strings. This carries report chrome ("Reporting
 * period | Q3 FY26") and the definitional lines ("% approved = 100% −
 * Non-MPA share"), which are not decoration — they are the only place
 * a reader learns what the headline number means.
 *
 * Every string arrives pre-resolved from the caller; this package has
 * no i18n runtime (same contract as the date pickers).
 */
export function MetaGrid({ items, columns = 2, bordered = true, entrance = true, className = '' }) {
  // Hooks before the early return per the motion rules.
  const riseStyle = useRiseInStyle(entrance);
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const cols =
    columns === 1 ? 'grid-cols-1' : columns === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  const shell = bordered
    ? 'rounded-[14px] border border-neutral-200 bg-white px-4 py-3 shadow-card'
    : '';
  return (
    <dl className={`grid grid-cols-1 gap-x-6 gap-y-2 ${cols} ${shell} ${className}`} style={riseStyle}>
      {list.map((item, i) => (
        <div key={item.label ?? i} className="flex items-baseline justify-between gap-3">
          <dt className="shrink-0 text-[11px] uppercase tracking-wide text-neutral-600">
            {item.label}
          </dt>
          <dd className="min-w-0 truncate text-right text-xs font-bold text-neutral-800">
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * `RankedList` — big muted rank number, bold title, muted subtitle,
 * optional trailing chip.
 *
 * Optional per-item extras (DSA prototype v7 lists): `dot` (a colour
 * swatch before the title, e.g. the retailer's colour), `value` (a big
 * bold figure tinted by `valueTone`), and `action` (a small trailing
 * button, e.g. "Ver"). `marker` swaps the rank number for an arrow
 * (`'up'` green, `'down'` red) or hides it (`'none'`). All absent =
 * the list renders exactly as before.
 *
 * Rank numbering existed ONLY inside two SVG charts (the ranked
 * horizontal bar and the lollipop), and both require a numeric value
 * and draw a bar. These rows have no bar — they have a severity chip.
 *
 * NEVER slices the data. When `maxHeight` is set the list WINDOWS the
 * DOM (fixed row height + spacers, the option-list pattern) and
 * scrolls the full set: "somos BI, no cortamos data" applies to render
 * caps too, not only to queries.
 */
export function RankedList({
  items,
  numbered = true,
  marker = 'number',
  layout = 'row',
  maxHeight,
  itemHeight = 56,
  ariaLabel,
  entrance = true,
  className = '',
}) {
  // Entrance (ronda 14): fade+rise of the whole list — no per-row
  // stagger (rows can be thousands, windowed). Hooks first.
  const riseStyle = useRiseInStyle(entrance);
  const [scrollTop, setScrollTop] = useState(0);
  const list = Array.isArray(items) ? items : [];
  const rowPx = Math.max(24, Number.isFinite(itemHeight) ? itemHeight : 56);
  const capped = typeof maxHeight === 'number' && maxHeight > 0;
  const windowed = capped && list.length * rowPx > maxHeight;

  // Visible window + overscan. Every row stays reachable by scrolling;
  // only the DOM is bounded. The spacer <li>s keep the scrollbar
  // honest about how much data is really there.
  const overscan = 3;
  const first = windowed ? Math.max(0, Math.floor(scrollTop / rowPx) - overscan) : 0;
  const visibleCount = windowed ? Math.ceil(maxHeight / rowPx) + overscan * 2 : list.length;
  const last = windowed ? Math.min(list.length, first + visibleCount) : list.length;
  const rows = windowed ? list.slice(first, last) : list;

  return (
    <ol
      className={`min-w-0 ${className}`}
      aria-label={ariaLabel}
      style={capped ? { maxHeight, overflowY: 'auto', ...riseStyle } : riseStyle}
      onScroll={windowed ? (e) => setScrollTop(e.currentTarget.scrollTop) : undefined}
    >
      {windowed && first > 0 && <li aria-hidden="true" style={{ height: first * rowPx }} />}
      {rows.map((item, i) => {
        const idx = (windowed ? first : 0) + i;
        const stack = layout === 'stack';
        return (
          <li
            key={item.id ?? idx}
            className={`flex gap-3 border-b border-neutral-100 last:border-b-0 ${stack ? 'items-start py-2' : 'items-center py-2.5'}`}
            style={windowed ? { height: rowPx } : undefined}
          >
            {numbered && marker === 'number' && (
              <span className="w-6 shrink-0 text-center text-sm font-bold tabular-nums text-neutral-500">
                {item.rank ?? idx + 1}
              </span>
            )}
            {numbered && (marker === 'up' || marker === 'down') && (
              <span
                aria-hidden="true"
                className="w-6 shrink-0 text-center text-lg font-extrabold"
                style={{ color: toneStyle(marker === 'up' ? 'good' : 'bad').fill }}
              >
                {marker === 'up' ? '↑' : '↓'}
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className={`text-sm font-semibold text-neutral-800 ${stack ? '' : 'truncate'}`}>
                {item.dot && (
                  <span
                    aria-hidden="true"
                    data-ranked-dot
                    className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-baseline"
                    style={{ background: item.dot }}
                  />
                )}
                {item.title}
              </div>
              {item.subtitle && (
                <div className={`text-xs text-neutral-600 ${stack ? '' : 'truncate'}`}>
                  {item.subtitle}
                </div>
              )}
            </div>
            {item.value != null && item.value !== '' && (
              <span
                data-ranked-value
                className="shrink-0 text-lg font-extrabold tabular-nums"
                style={{ color: toneStyle(item.valueTone).fg }}
              >
                {item.value}
                {item.valueToneLabel && <span className="sr-only"> ({item.valueToneLabel})</span>}
              </span>
            )}
            {item.chip && (
              <StatusChip
                label={item.chip.label}
                toneSpec={{ tone: item.chip.tone }}
                toneLabel={item.chip.toneLabel}
                className="shrink-0"
              />
            )}
            {item.trailing && <div className="shrink-0">{item.trailing}</div>}
            {item.action && (
              <button
                type="button"
                onClick={item.action.onClick}
                className="shrink-0 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
              >
                {item.action.label}
              </button>
            )}
          </li>
        );
      })}
      {windowed && last < list.length && (
        <li aria-hidden="true" style={{ height: (list.length - last) * rowPx }} />
      )}
    </ol>
  );
}

/**
 * `Callout` — a tinted message surface with an icon slot, a title and
 * free children.
 *
 * The only tinted message surface in the monorepo is `MessageCard`
 * (dashboard-render), which is documented as the error / no-data /
 * invalid-layout card — a SYSTEM-state surface. This is the AUTHORED
 * one: the red "Action needed" banner, the per-card "Priority action"
 * footer, the footnote disclaimer.
 *
 * TWO COLOUR AXES on purpose. `tone` is the THRESHOLD axis (a
 * judgement about a number). `accent` is decorative and can never be
 * reached by banding — which is exactly why `'info'` is absent from
 * the `Tone` vocabulary.
 *
 * `aiGenerated` renders a visible marker. Required whenever the body
 * comes from the AI-analysis endpoint: prose that no fact table can
 * produce must never look indistinguishable from a measured number.
 * `aiLabel` is caller-supplied because this package has no i18n.
 */
export function Callout({
  tone = 'neutral',
  accent,
  icon,
  title,
  children,
  dense = false,
  role = 'note',
  aiGenerated = false,
  aiLabel = 'AI',
  entrance = true,
  className = '',
  action,
}) {
  // Hooks first (motion rules).
  const riseStyle = useRiseInStyle(entrance);
  const palette = toneStyle(tone);
  // `accent` overrides the tint but NOT the semantics: a decorative
  // violet panel is still a note, never a threshold judgement.
  const info = accent === 'info';
  // Look v3 (2026-10-24): a quiet tinted surface behind ONE uniform
  // border; the tone lives in the icon and the title (shape and word).
  const bg = info ? ACCENT_INFO.surface : palette.surface;
  const fg = info ? ACCENT_INFO.fg : palette.fg;
  const pad = dense ? 'px-3 py-2' : 'px-5 py-[18px]';

  return (
    <div
      data-callout={info ? 'info' : tone}
      role={role === 'alert' ? 'alert' : 'note'}
      className={`rounded-[11px] border border-neutral-200 ${pad} ${className}`}
      style={{ background: bg, ...riseStyle }}
    >
      <div className="flex items-start gap-3">
        {icon && (
          <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-primary-line bg-white" style={{ color: fg }} aria-hidden="true">
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1">
          {(title || aiGenerated) && (
            <div className="mb-0.5 flex items-center gap-2">
              {title && (
                <span
                  className={dense ? 'text-2xs uppercase tracking-wide' : 'font-display text-sm font-extrabold'}
                  style={{ color: fg }}
                >
                  {title}
                </span>
              )}
              {aiGenerated && (
                <span className="rounded-full border border-neutral-200 bg-white/70 px-1.5 py-0 text-2xs font-bold text-neutral-500">
                  {aiLabel}
                </span>
              )}
            </div>
          )}
          <div
            className={dense ? 'text-xs font-semibold leading-snug text-neutral-800' : 'text-xs leading-[1.75]'}
            style={dense ? undefined : { color: NEUTRAL.muted }}
          >
            {children}
          </div>
          {action && (
            // Look v3: the buttons a callout carries are quiet outlined
            // actions, whatever plain element the caller passes.
            <div
              data-callout-action=""
              className="flex flex-wrap gap-2.5 pt-3 [&_a]:rounded-lg [&_a]:border [&_a]:border-primary-line [&_a]:bg-white [&_a]:px-2.5 [&_a]:py-1.5 [&_a]:text-2xs [&_a]:font-semibold [&_a]:text-primary [&_button]:rounded-lg [&_button]:border [&_button]:border-primary-line [&_button]:bg-white [&_button]:px-2.5 [&_button]:py-1.5 [&_button]:text-2xs [&_button]:font-semibold [&_button]:text-primary"
            >
              {action}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * `MetricStrip` — a full-width band of 1..4 metric blocks.
 *
 * The shape the client's "Regional Health" row asks for and that no
 * existing widget covers: it is not a KPI card (those are equal tiles
 * in a strip), not a chart (no axes), and not `metric_rows` (those are
 * a vertical stack of meters). It is a titled BAND: a label column on
 * the left, N blocks across the rest, and an optional note on the far
 * right.
 *
 * Blocks lay out side by side from `md` up and STACK on mobile —
 * squeezing four meters into a phone width makes every bar unreadable,
 * which is worse than scrolling.
 */
export function MetricStrip({
  title,
  subtitle,
  items,
  note,
  max = 100,
  format = 'raw',
  decimals = 1,
  entrance = true,
  className = '',
}) {
  // Entrance (ronda 14): the band's TEXT fades+rises (durBase); the
  // internal MeterBars keep their own growX entrance (durEntrance).
  // Hooks before the early return per the motion rules.
  const riseStyle = useRiseInStyle(entrance);
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  return (
    <div
      className={`flex flex-col gap-4 rounded-[14px] border border-neutral-200 bg-white px-5 py-4 shadow-card md:flex-row md:items-start ${className}`}
      style={riseStyle}
    >
      {(title || subtitle) && (
        <div className="w-full shrink-0 md:w-48">
          {title && <p className="text-sm font-bold text-neutral-800">{title}</p>}
          {subtitle && <p className="mt-0.5 text-xs text-neutral-600">{subtitle}</p>}
        </div>
      )}
      {/* Flex, not grid: `auto-fit` kept reserving four tracks for two
          blocks, so a 2-item band bunched into the left half with dead
          space beside it (measured in staging: 4 × 371px for 2 items).
          `flex-1` + a min width splits the row evenly for ANY count of
          1..4, and wraps — which on a phone means one block per row,
          the stacking the band was specified to do. */}
      <div className="flex min-w-0 flex-1 flex-wrap gap-x-8 gap-y-4">
        {list.map((item, i) => {
          const hasValue =
            typeof item.value === 'number' && Number.isFinite(item.value);
          return (
            <div key={item.label ?? i} className="min-w-0 flex-1 basis-[180px] space-y-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-semibold text-neutral-700">
                  {item.label}
                </span>
                <span className="shrink-0 text-sm font-bold tabular-nums text-neutral-800">
                  {item.valueLabel ??
                    (hasValue ? formatValue(item.value, format, decimals) : '—')}
                </span>
              </div>
              <MeterBar
                value={hasValue ? item.value : null}
                max={max}
                toneSpec={item.toneSpec}
                ariaLabel={String(item.label ?? '')}
                toneLabel={item.toneLabel}
              />
              {item.delta && (
                <p
                  className="text-[11px] font-semibold"
                  style={item.deltaTone ? { color: toneStyle(item.deltaTone).fg } : undefined}
                >
                  {item.delta}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {note && (
        <p className="w-full shrink-0 text-[11px] leading-snug text-neutral-600 md:w-56 md:text-right">
          {note}
        </p>
      )}
    </div>
  );
}
